import styled, { keyframes, css } from 'styled-components';
import { monserratSemiBold, robotoRegular } from '@habitech/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f4f5f7;
`;

export const WelcomeSection = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.75rem 0;
`;

export const WelcomeTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${monserratSemiBold};
  letter-spacing: -0.3px;

  span {
    color: #0ea5e9;
  }
`;

export const WelcomeSubtitle = styled.p`
  margin: 0.2rem 0 0;
  font-size: 13px;
  color: #6b7280;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Navbar — AWS-style dark bar
// ─────────────────────────────────────────────────────────────────────────────
export const NavBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: #0f1923;
  border-bottom: 1px solid #1e2d3d;
  height: 52px;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

export const NavBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

export const NavDivider = styled.div`
  width: 1px;
  height: 18px;
  background: #1e2d3d;
  flex-shrink: 0;
`;

export const NavTitle = styled.h1`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  font-family: ${monserratSemiBold};
  white-space: nowrap;
`;

export const NavSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: #8ba3bc;
  font-family: ${robotoRegular};
`;

export const NavCenter = styled.div`
  flex: 1;
  max-width: 440px;
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

export const NavEmail = styled.span`
  font-size: 12px;
  color: #8ba3bc;
  font-family: ${robotoRegular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

export const PageFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fffbeb;
  border-top: 1px solid #fde68a;
  color: #92400e;
  font-size: 12px;
  font-family: ${robotoRegular};
  padding: 6px 2rem;
  text-align: center;
  z-index: 90;
`;

export const EnvBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color + '15'};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color + '35'};
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${robotoRegular};
`;

export const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid #1e2d3d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  color: #8ba3bc;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  font-family: ${robotoRegular};
  white-space: nowrap;

  &:hover {
    border-color: #ff6b6b;
    color: #ff6b6b;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Search bar
// ─────────────────────────────────────────────────────────────────────────────
export const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
`;

export const SearchInputBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid #1e2d3d;
  border-radius: 6px;
  padding: 6px 12px;
  transition: border-color 0.15s, background 0.15s;

  &:focus-within {
    border-color: #4a6fa5;
    background: rgba(255, 255, 255, 0.11);
  }
`;

export const SearchIconWrapper = styled.span`
  color: #8ba3bc;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 13px;
  color: #ffffff;
  background: transparent;
  font-family: ${robotoRegular};

  &::placeholder {
    color: #8ba3bc;
  }
`;

export const SearchDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #111d2b;
  border: 1px solid #1e2d3d;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 50;
  overflow: hidden;
`;

export const SearchDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 9px 14px;
  cursor: pointer;
  border-bottom: 1px solid #1a2a3a;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #162233;
  }
`;

export const SearchDropdownName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #f0f6fc;
  font-family: ${robotoRegular};
  flex: 1;
`;

export const SearchDropdownDescription = styled.span`
  font-size: 12px;
  color: #7a9bb8;
  font-family: ${robotoRegular};
`;

export const SearchEmpty = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: 13px;
  color: #7a9bb8;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Panel — contenedor tipo AWS widget
// ─────────────────────────────────────────────────────────────────────────────
export const Panel = styled.div`
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(229, 231, 235, 0.6);
  box-shadow: 0 4px 24px rgba(91, 33, 182, 0.07), 0 1px 4px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  transition: height 0.25s ease;
`;

export const PanelHeader = styled.div`
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 20px 20px 0 0;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  font-family: ${monserratSemiBold};
  letter-spacing: -0.2px;
`;

export const PanelSubtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 12px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// List container — área scrolleable dentro del panel
// ─────────────────────────────────────────────────────────────────────────────
export const ListContainer = styled.div`
  flex: 1;
  overflow-y: hidden;
  &[data-scrollable='true'] {
    overflow-y: auto;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// List item — fila clickeable estilo AWS
// ─────────────────────────────────────────────────────────────────────────────
export const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.6875rem 1.5rem;
  cursor: pointer;
  border-bottom: 1px solid #f9fafb;
  transition: background 0.12s, padding-left 0.12s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #faf9ff;
    padding-left: 1.75rem;
  }
`;

export const ListItemIcon = styled.div<{ $color?: string }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
  background: ${({ $color }) => ($color ? $color + '12' : '#e5e7eb')};
  border: 1px solid ${({ $color }) => ($color ? $color + '22' : '#d1d5db')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color ?? '#7c3aed'};
  flex-shrink: 0;
`;

export const ListItemName = styled.span`
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  font-family: ${robotoRegular};
  flex: 1;
`;

export const ListItemDescription = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Panel footer — "Ver todos / Ver menos"
// ─────────────────────────────────────────────────────────────────────────────
export const PanelFooter = styled.button`
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid #f3f4f6;
  border-radius: 0 0 20px 20px;
  padding: 0.75rem 1.5rem;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #7c3aed;
  cursor: pointer;
  font-family: ${robotoRegular};
  transition: background 0.1s;
  margin-top: auto;

  &:hover {
    background: #faf9ff;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SSO badge — inline junto al nombre
// ─────────────────────────────────────────────────────────────────────────────
export const SsoBadge = styled.span`
  color: #f97316;
  font-size: 11px;
  font-weight: 700;
  font-family: ${robotoRegular};
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  margin-left: 4px;
`;

export const VpnBadge = styled.span`
  color: #ef4444;
  font-size: 11px;
  font-weight: 700;
  font-family: ${robotoRegular};
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  margin-left: 4px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Estado "abriendo herramienta" — inline en la fila
// ─────────────────────────────────────────────────────────────────────────────
export const OpeningRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  border-bottom: 1px solid #f9fafb;
  color: #5b21b6;
  font-size: 13px;
  font-family: ${monserratSemiBold};

  &:last-child {
    border-bottom: none;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Banner localhost
// ─────────────────────────────────────────────────────────────────────────────
export const LocalhostBanner = styled.div`
  background: #fffbeb;
  border-bottom: 1px solid #fcd34d;
  padding: 7px 1.25rem;
  font-size: 12px;
  color: #92400e;
  font-family: ${robotoRegular};

  strong {
    font-family: ${monserratSemiBold};
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton — placeholders animados para carga de módulos
// ─────────────────────────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const skeletonBase = css`
  background: linear-gradient(90deg, #f3f4f6 25%, #e9eaec 50%, #f3f4f6 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: 6px;
`;

export const SkeletonListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.6875rem 1.5rem;
  border-bottom: 1px solid #f9fafb;

  &:last-child {
    border-bottom: none;
  }
`;

export const SkeletonIcon = styled.div`
  ${skeletonBase}
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
`;

export const SkeletonName = styled.div`
  ${skeletonBase}
  height: 13px;
  flex: 1;
  max-width: 140px;
`;

export const SkeletonDescription = styled.div`
  ${skeletonBase}
  height: 11px;
  width: 90px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Hover Preview — aparece debajo de los paneles al hacer hover en un item
// ─────────────────────────────────────────────────────────────────────────────
export const HoverPreviewWrapper = styled.div<{ $active: boolean }>`
  margin-top: 1rem;
  position: relative;
  height: 120px;
  padding: 18px 2rem;
  background: ${({ $active }) => ($active ? '#0b1622' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? '#1e3448' : 'transparent')};
  border-radius: 10px;
  pointer-events: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  transition: background 0.2s ease, border-color 0.2s ease;
`;

export const HoverPreviewPlaceholder = styled.div<{ $visible: boolean }>`
  display: none;
`;

export const HoverPreviewContent = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(6px)')};
  transition: opacity 0.22s ease, transform 0.22s ease;
`;

export const HoverPreviewName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #c8dff0;
  font-family: ${monserratSemiBold};
  letter-spacing: 0.1px;
`;

export const HoverPreviewSummary = styled.div`
  font-size: 12px;
  color: #7a9db8;
  font-family: ${robotoRegular};
  line-height: 1.55;
`;

export const HoverPreviewChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
`;

export const HoverPreviewChip = styled.div<{ $color?: string }>`
  background: ${({ $color }) => ($color ? $color + '18' : 'rgba(255,255,255,0.05)')};
  border: 1px solid ${({ $color }) => ($color ? $color + '40' : 'rgba(255,255,255,0.12)')};
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  color: ${({ $color }) => $color ?? '#c8d6e3'};
  font-family: ${robotoRegular};
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ $color }) => $color ?? '#4a6a84'};
    flex-shrink: 0;
  }
`;

